import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../config";
import { Loader2, Download, Sparkles, Copy, Check, Image, RefreshCw } from "lucide-react";

const SERIES = [
  { name: "Before/After", color: "bg-red-500", ads: ["ad1_before_after_ga", "ad2_chatgpt_recommends", "ad3_blog_agent_da"] },
  { name: "Strach (FOMO)", color: "bg-amber-500", ads: ["ad4_73_percent", "ad5_competitor_spy", "ad6_cookie_gdpr"] },
  { name: "Porovnanie", color: "bg-emerald-500", ads: ["ad7_semrush_killer", "ad8_18_tools"] },
  { name: "Social Proof", color: "bg-blue-500", ads: ["ad9_721_percent", "ad10_page5_to_page1"] },
];

export default function AdminAdsTab() {
  const [templates, setTemplates] = useState([]);
  const [generated, setGenerated] = useState({});
  const [generating, setGenerating] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    try {
      const res = await axios.get(`${API}/ads/templates`);
      setTemplates(res.data.templates || []);
    } catch (e) { console.error(e); }
  };

  const generateAd = async (adId) => {
    setGenerating(adId);
    try {
      const res = await axios.post(`${API}/ads/generate`, { ad_id: adId, prompt: "" }, { timeout: 120000 });
      if (res.data.image_base64) {
        setGenerated(prev => ({ ...prev, [adId]: `data:image/png;base64,${res.data.image_base64}` }));
      }
    } catch (e) { console.error(e); }
    setGenerating(null);
  };

  const downloadImage = (adId) => {
    const img = generated[adId];
    if (!img) return;
    const a = document.createElement("a");
    a.href = img;
    a.download = `${adId}.png`;
    a.click();
  };

  const copyText = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getTemplate = (adId) => templates.find(t => t.id === adId) || {};

  return (
    <div className="space-y-8" data-testid="admin-ads-tab">
      <div>
        <h2 className="text-xl font-bold">FB & IG Reklamy</h2>
        <p className="text-sm text-gray-500">10 reklamných vizuálov s textami v SK a EN. Kliknite "Generovať" pre AI vizuál.</p>
      </div>

      {SERIES.map((series, si) => (
        <div key={si}>
          <div className="flex items-center gap-2 mb-4">
            <span className={`w-3 h-3 rounded-full ${series.color}`} />
            <h3 className="font-bold text-sm uppercase text-gray-600">{series.name}</h3>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {series.ads.map(adId => {
              const t = getTemplate(adId);
              const img = generated[adId];
              const isGenerating = generating === adId;
              return (
                <div key={adId} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`ad-card-${adId}`}>
                  {/* Image area */}
                  <div className="aspect-[3/2] bg-gray-100 relative flex items-center justify-center">
                    {img ? (
                      <img src={img} alt={t.title_en} className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center p-4">
                        <Image className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p className="text-xs text-gray-400">Kliknite "Generovať" pre vizuál</p>
                      </div>
                    )}
                    {isGenerating && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <div className="text-center">
                          <Loader2 className="w-8 h-8 animate-spin text-white mx-auto mb-2" />
                          <p className="text-white text-xs">Generujem AI vizuál...</p>
                          <p className="text-gray-400 text-[10px]">(~30-60 sekúnd)</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4 space-y-3">
                    {/* SK */}
                    <div>
                      <div className="flex items-center gap-1 mb-1">
                        <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 font-bold rounded">SK</span>
                        <span className="text-xs font-bold text-gray-700">{t.title_sk}</span>
                      </div>
                      <p className="text-xs text-gray-500">{t.desc_sk}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-600 font-mono">CTA: {t.cta_sk}</span>
                        <button onClick={() => copyText(`${t.title_sk}\n${t.desc_sk}\n\n${t.cta_sk}`, `${adId}_sk`)} className="text-gray-400 hover:text-blue-600" data-testid={`copy-${adId}-sk`}>
                          {copiedId === `${adId}_sk` ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                        </button>
                      </div>
                    </div>

                    {/* EN */}
                    <div>
                      <div className="flex items-center gap-1 mb-1">
                        <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 font-bold rounded">EN</span>
                        <span className="text-xs font-bold text-gray-700">{t.title_en}</span>
                      </div>
                      <p className="text-xs text-gray-500">{t.desc_en}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-600 font-mono">CTA: {t.cta_en}</span>
                        <button onClick={() => copyText(`${t.title_en}\n${t.desc_en}\n\n${t.cta_en}`, `${adId}_en`)} className="text-gray-400 hover:text-blue-600" data-testid={`copy-${adId}-en`}>
                          {copiedId === `${adId}_en` ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                        </button>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2 border-t border-gray-100">
                      <button
                        onClick={() => generateAd(adId)}
                        disabled={isGenerating}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white text-xs font-bold rounded hover:bg-blue-500 disabled:opacity-50"
                        data-testid={`generate-${adId}`}
                      >
                        {isGenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        {isGenerating ? "Generujem..." : img ? "Pregenerovať" : "Generovať"}
                      </button>
                      {img && (
                        <button onClick={() => downloadImage(adId)} className="flex items-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 text-xs font-bold rounded hover:bg-gray-200" data-testid={`download-${adId}`}>
                          <Download className="w-3 h-3" /> Stiahnuť
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
